export type AnalyticsEventParams = Record<string, string | number | boolean | null | undefined>;

const GA_MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
const ANALYTICS_DEBUG = (process.env.REACT_APP_ANALYTICS_DEBUG || '').toLowerCase() === 'true';

let initialized = false;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

const ensureGtagScript = (): void => {
  if (!isBrowser()) {
    return;
  }

  const existingScript = document.getElementById('wpt-ga4-script');
  if (existingScript) {
    return;
  }

  const script = document.createElement('script');
  script.id = 'wpt-ga4-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(script);
};

const ensureGtagFunction = (): void => {
  if (!isBrowser()) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = (...args: unknown[]) => {
      window.dataLayer?.push(args);
    };
  }
};

const sendDebugLog = (eventName: string, params?: AnalyticsEventParams): void => {
  if (!ANALYTICS_DEBUG) {
    return;
  }
  // eslint-disable-next-line no-console
  console.info('[analytics]', eventName, params || {});
};

export const initAnalytics = (): void => {
  if (initialized || !isBrowser() || !GA_MEASUREMENT_ID) {
    return;
  }

  ensureGtagScript();
  ensureGtagFunction();

  window.gtag?.('js', new Date());
  window.gtag?.('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true
  });

  initialized = true;
};

export const trackEvent = (eventName: string, params: AnalyticsEventParams = {}): void => {
  if (!isBrowser()) {
    return;
  }

  if (!GA_MEASUREMENT_ID) {
    sendDebugLog(eventName, params);
    return;
  }

  initAnalytics();
  window.gtag?.('event', eventName, params);
  sendDebugLog(eventName, params);
};

export const trackPageView = (pathWithQuery: string): void => {
  if (!isBrowser()) {
    return;
  }

  trackEvent('page_view', {
    page_path: pathWithQuery,
    page_location: window.location.href,
    page_title: document.title
  });
};
